import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { ArrowLeft, Car, Loader2, CheckCircle2, AlertCircle, Hash, Palette, Tag } from 'lucide-react';
interface VehicleForm {
  plate: string;
  vehicle_type: string;
  brand: string;
  model: string;
  color: string;
  is_active: boolean;
}
export function CreateVehiclePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<VehicleForm>({
    plate: '',
    vehicle_type: 'moto',
    brand: '',
    model: '',
    color: '',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const updateField = (field: keyof VehicleForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!form.plate.trim()) return setError('La placa es obligatoria.');
    setIsSubmitting(true);
    try {
      const {
        error: insertError
      } = await supabase.from('vehicles').insert({
        plate: form.plate.trim().toUpperCase(),
        vehicle_type: form.vehicle_type,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        color: form.color.trim() || null,
        is_active: form.is_active
      });
      if (insertError) throw insertError;
      setSuccess(true);
    } catch (err: any) {
      console.error('[CreateVehicle] Error:', err);
      setError(err.message || 'Error al registrar el vehículo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (success) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-2100">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-2101">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-2102">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-2103" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-2104">¡Vehículo registrado!</h1>
          <p className="text-white/50" data-id="element-2105">
            El vehículo {form.plate.toUpperCase()} fue guardado exitosamente.
          </p>

          <div className="flex gap-3 pt-4" data-id="element-2106">
            <button onClick={() => {
            setSuccess(false);
            setForm({
              plate: '',
              vehicle_type: 'moto',
              brand: '',
              model: '',
              color: '',
              is_active: true
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-2107">
              Registrar otro
            </button>
            <button onClick={() => navigate('/admin/vehicles')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-2108">
              Ver vehículos
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-2109">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-2110">
        <DriverAdminTabs data-id="element-2111" />

        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-2112">
          <button onClick={() => navigate('/admin/vehicles')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-2113">
            <ArrowLeft className="w-5 h-5" data-id="element-2114" />
          </button>
          <div data-id="element-2115">
            <h1 className="text-2xl font-bold" data-id="element-2116">Nuevo Vehículo</h1>
            <p className="text-white/40 text-sm" data-id="element-2117">
              Registra un vehículo en la plataforma
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-id="element-2118">
          {/* Plate */}
          <div data-id="element-2119">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2120">
              Placa (Requerido)
            </label>
            <div className="relative" data-id="element-2121">
              <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-2122" />
              <input type="text" value={form.plate} onChange={e => updateField('plate', e.target.value)} placeholder="ABC-123" className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white font-mono uppercase placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" required data-id="element-2123" />
            </div>
          </div>

          {/* Vehicle Type */}
          <div data-id="element-2124">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2125">
              Tipo de Vehículo
            </label>
            <select value={form.vehicle_type} onChange={e => updateField('vehicle_type', e.target.value)} className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-4 text-base text-white focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] appearance-none" data-id="element-2126">
              <option value="moto" data-id="element-2127">🏍️ Moto</option>
              <option value="carro" data-id="element-2128">🚗 Carro</option>
              <option value="bicicleta" data-id="element-2129">🚲 Bicicleta</option>
              <option value="otro" data-id="element-2130">📦 Otro</option>
            </select>
          </div>

          {/* Brand & Model */}
          <div className="grid grid-cols-2 gap-4" data-id="element-2131">
            <div data-id="element-2132">
              <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2133">
                Marca
              </label>
              <div className="relative" data-id="element-2134">
                <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-2135" />
                <input type="text" value={form.brand} onChange={e => updateField('brand', e.target.value)} placeholder="Honda" className="w-full bg-[#141414] border border-white/10 rounded-xl pl-10 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-2136" />
              </div>
            </div>
            <div data-id="element-2137">
              <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2138">
                Modelo
              </label>
              <input type="text" value={form.model} onChange={e => updateField('model', e.target.value)} placeholder="Civic" className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-2139" />
            </div>
          </div>

          {/* Color */}
          <div data-id="element-2140">
            <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-2141">
              Color
            </label>
            <div className="relative" data-id="element-2142">
              <Palette className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" data-id="element-2143" />
              <input type="text" value={form.color} onChange={e => updateField('color', e.target.value)} placeholder="Rojo" className="w-full bg-[#141414] border border-white/10 rounded-xl pl-10 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-2144" />
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-2145">
            <span className="text-sm font-medium text-white/70" data-id="element-2146">
              Vehículo activo
            </span>
            <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-2147">
              <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-2148" />
            </button>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-2149">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-2150" />
              <div className="text-sm whitespace-pre-line" data-id="element-2151">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-2152">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-2153" />
                Guardando...
              </> : <>
                <Car className="w-6 h-6" data-id="element-2154" />
                Registrar Vehículo
              </>}
          </button>
        </form>
      </div>
    </div>;
}